/**
 * Main Index Page Logic
 *
 * Dashboard/home page with navigation buttons
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { initAuthenticatedPage, hasPermission, hideLoading } from '../app/init.js';
import { connectHostMonitor, connectRemoteHostMonitors } from '../host/resources.js';
import { userSettings, doLogout } from '../user/settings.js';
import { adminSettings } from '../admin/settings.js';

/**
 * Navigate to VMs page
 */
export function goToVms(): void {
  window.location.href = '/vms';
}

/**
 * Navigate to disk manager
 */
export function goToDiskman(): void {
  window.location.href = '/diskman';
}

/**
 * Navigate to user management
 */
export function goToUsers(): void {
  window.location.href = '/users';
}

/**
 * Navigate to network management
 */
export function goToNetworks(): void {
  window.location.href = '/networks';
}

/**
 * Navigate to storage management
 */
export function goToStorage(): void {
  window.location.href = '/storage';
}

/**
 * Navigate to remote host management
 */
export function goToHosts(): void {
  window.location.href = '/hosts';
}

/**
 * Navigate to organization management
 */
export function goToOrganizations(): void {
  window.location.href = '/organizations';
}

/**
 * Navigate to event log
 */
export function goToEvents(): void {
  window.location.href = '/events';
}

/**
 * Show VPN access dialog
 */
export async function vpnAccess(): Promise<void> {
  const vpnConfig = await apiCall(api.getVpnConfig(), 'VPN Access');
  if (!vpnConfig) return;

  const html = `
    <div style="padding: 10px;">
      <h4>VPN Configuration</h4>
      <p>Download your VPN configuration file to connect to the virtual machines network.</p>
      ${vpnConfig.download_url ? `<a class="ui-button" href="${vpnConfig.download_url}" download>Download VPN Config</a>` : '<p>VPN configuration not available.</p>'}
      ${vpnConfig.instructions ? `<div style="margin-top: 10px;"><h5>Instructions:</h5><p>${vpnConfig.instructions}</p></div>` : ''}
    </div>
  `;

  $('<div></div>')
    .appendTo('body')
    .html(html)
    .dialog({
      modal: true,
      title: 'VPN Access',
      width: 400,
      buttons: {
        Close: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Initialize the main page
 */
export async function initMainPage(): Promise<void> {
  const user = await initAuthenticatedPage();
  if (!user) return; // Will redirect to login

  // Hide loading spinner and show content
  hideLoading();

  // Show resource bars if user has permission
  if (hasPermission('view-resources')) {
    connectHostMonitor();
    connectRemoteHostMonitors();
  }
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).goToVms = goToVms;
  (window as unknown as Record<string, unknown>).goToDiskman = goToDiskman;
  (window as unknown as Record<string, unknown>).goToUsers = goToUsers;
  (window as unknown as Record<string, unknown>).goToNetworks = goToNetworks;
  (window as unknown as Record<string, unknown>).goToStorage = goToStorage;
  (window as unknown as Record<string, unknown>).goToHosts = goToHosts;
  (window as unknown as Record<string, unknown>).goToOrganizations = goToOrganizations;
  (window as unknown as Record<string, unknown>).goToEvents = goToEvents;
  (window as unknown as Record<string, unknown>).vpnAccess = vpnAccess;
  (window as unknown as Record<string, unknown>).userSettings = userSettings;
  (window as unknown as Record<string, unknown>).adminSettings = adminSettings;
  (window as unknown as Record<string, unknown>).doLogout = doLogout;
  (window as unknown as Record<string, unknown>).initMainPage = initMainPage;
}
